import React, { useEffect, useState } from 'react';
import { Typography, Box, Grid } from '@mui/material';

import serviceMonitorService from '../../service/serviceMonitor.service';
import masterDataService from '../../service/masterData.service';
import { AutoCompleteSelectUI, DebouncedInputUI, FormLabelUI, PhoneBase } from '../Interface';
import useParam from '../../context/ParamContext';
import ServiceMonitorCard from './ServiceMonitorCard';
import { generateAutocompleteItems } from '../../utils/helper';

import { useLoader } from '../../provider/LoaderProvider';
import ServiceMonitorWorkAreaDialog from './ServiceMonitorWorkAreaDialog';
import ConfirmDialogV2UI from '../Interface/ConfirmDialogV2UI';
import ServiceMonitorReportDialog from './ServiceMonitorReportDialog';

const ServiceMonitorPage = () => {
  const { showLoader, hideLoader } = useLoader();
  const { getParam, params } = useParam();
  const [serviceTasks, setServiceTasks] = useState({ rows: [], rowCount: 0 });
  const [autoCompleteOptions, setAutoCompleteOptions] = useState({
    workArea: [],
    project: [],
    operation: [],
    user: [],
  });
  const [helperData, setHelperData] = useState({});
  const [refetch, setRefetch] = useState(false);

  const defaultFilter = {
    workAreaId: null,
    s_projectId: null,
    st_serviceOperationId: null,
    responsiblePersonId: null,
    s_workingNumber: null,
    s_serviceStatusId: ['APPROVED', 'INPROGRESS', 'INTERRUPTED'],
  };

  const [filter, setFilter] = useState(defaultFilter);

  const filterOperators = {
    workAreaId: 'equals',
    s_projectId: 'equals',
    st_serviceOperationId: 'equals',
    responsiblePersonId: 'equals',
    s_workingNumber: 'contains',
    s_serviceStatusId: 'isAnyOf',
  };

  const [query, setQuery] = useState({
    filter: {
      filterModel: {
        items: [],
        logicOperator: 'and',
      },
    },
    sorting: {
      sortModel: [{ field: 'st_id', sort: 'desc' }],
    },
    pagination: {
      paginationModel: {},
    },
  });
  const [showDialog, setShowDialog] = useState(false);

  const defaultConfirmDialogData = {
    open: false,
    message: '',
    buttons: [
      {
        label: 'Megerősít',
        variant: 'outlined',
        color: 'inherit',
        onClick: () => {},
      },
    ],
  };

  const [confirmDialogData, setConfirmDialogData] = useState(defaultConfirmDialogData);

  const defaultReportDialogData = {
    open: false,
    selectedTaskId: null,
  };

  const [reportDialogData, setReportDialogData] = useState(defaultReportDialogData);

  const getList = () => {
    showLoader();
    serviceMonitorService
      .getTasks(query)
      .then((data) => {
        setServiceTasks(data);
      })
      .finally(() => {
        hideLoader();
      });
  };

  useEffect(() => {
    if (!filter.workAreaId) {
      setShowDialog(true);
    }
  }, [filter.workAreaId]);

  useEffect(() => {
    if (!Object.keys(params).length) getParam();
    initDefaultValues();
  }, []);

  useEffect(() => {
    if (Object.keys(params).length > 0 && filter.workAreaId) {
      handleFilterChange();
    }
  }, [params, filter]);

  useEffect(() => {
    if (query.filter.filterModel.items.length > 0) {
      getList();
    }
  }, [query]);

  useEffect(() => {
    if (refetch && query.filter.filterModel.items.length > 0) {
      getList();
      setRefetch(false);
    }
  }, [refetch]);

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .workAreaToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .serviceOperationToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .usersToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .interruptionReasonsToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          workArea: resp[0],
          project: resp[1],
          serviceOperation: resp[2],
          user: resp[3],
          interruption: resp[4],
        };
        setHelperData(aData);

        const filteredProject = aData.project.filter((p) => p.active);

        const acOptions = {
          workArea: generateAutocompleteItems(aData.workArea, 'name', 'code'),
          project: generateAutocompleteItems(filteredProject, ['projectName', 'orderNumber'], 'projectId'),
          serviceOperation: generateAutocompleteItems(aData.serviceOperation, ['code', 'name'], 'id'),
          user: generateAutocompleteItems(aData.user, 'nickName', 'userId'),
        };

        setAutoCompleteOptions((prev) => ({
          ...prev,
          ...acOptions,
        }));
      })
      .finally(() => {
        //hideLoader();
      });
  };

  const handleFilterChange = () => {
    setQuery((prev) => {
      const validFilters = Object.entries(filter)
        .filter(([_, value]) => value !== null && value !== undefined)
        .map(([key, value]) => ({
          field: key,
          operator: filterOperators[key] || 'equals',
          value: value,
        }));

      return {
        ...prev,
        filter: {
          filterModel: {
            items: validFilters,
          },
        },
      };
    });
  };

  const emptyFilter = () => {
    setFilter(defaultFilter);
  };

  const hideConfirmDialog = () => {
    setConfirmDialogData({ ...defaultConfirmDialogData });
  };

  const hideReportDialog = () => {
    setReportDialogData({ ...defaultReportDialogData });
  };

  return (
    <PhoneBase name="ServiceMonitor">
      <Box
        sx={{
          backgroundColor: '#E9F5FE',
          height: 'calc(100vh)',
        }}
      >
        <Grid container spacing={1} sx={{ px: 1, mb: 1 }}>
          <Grid item xs={3}>
            <DebouncedInputUI
              label="Munkalap szám"
              debounceMs={800}
              limitTags={3}
              quickFilterSearchValue={filter?.s_workingNumber}
              setQuickFilterSearchValue={(newValue) => {
                setFilter((prevState) => ({
                  ...prevState,
                  s_workingNumber: newValue ?? null,
                }));
              }}
            />
          </Grid>
          <Grid item xs={3}>
            <AutoCompleteSelectUI
              label={<FormLabelUI text="Project" />}
              dataset={autoCompleteOptions.project}
              onChange={(_e, newVal) => {
                setFilter((prevState) => ({
                  ...prevState,
                  s_projectId: newVal?.value ?? null,
                }));
              }}
              selectedValue={filter.s_projectId}
            />
          </Grid>
          <Grid item xs={3}>
            <AutoCompleteSelectUI
              label={<FormLabelUI text="Művelet" />}
              dataset={autoCompleteOptions.serviceOperation}
              onChange={(_e, newVal) => {
                setFilter((prevState) => ({
                  ...prevState,
                  st_serviceOperationId: newVal?.value ?? null,
                }));
              }}
              selectedValue={filter.st_serviceOperationId}
            />
          </Grid>
          <Grid item xs={3} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box
              onClick={() => {
                emptyFilter();
                setShowDialog(true);
              }}
              sx={{
                mt: 1,
                height: '40px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                color: 'rgb(22, 163, 74)',
                backgroundColor: 'rgba(187, 247, 208, 0.3)',
                border: '2px solid rgb(22, 163, 74)',
                borderRadius: '12px',
                fontSize: '2rem',
                fontWeight: 700,
                cursor: 'pointer',
                userSelect: 'none',
                width: '60px',
              }}
            >
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, lineHeight: 1 }}>
                {helperData?.workArea?.find((area) => area.id === filter.workAreaId)?.code}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Grid
          container
          spacing={1}
          direction="row"
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
            px: 0.5,
          }}
        >
          {serviceTasks.rows.length > 0 &&
            serviceTasks.rows.map((task) => (
              <ServiceMonitorCard
                key={task.st_id}
                data={task}
                setRefetch={setRefetch}
                rows={serviceTasks.rows}
                params={params}
                helperData={helperData}
                autoCompleteOptions={autoCompleteOptions}
                setConfirmDialogData={setConfirmDialogData}
                hideConfirmDialog={hideConfirmDialog}
                setReportDialogData={setReportDialogData}
              />
            ))}
        </Grid>
      </Box>
      <ServiceMonitorWorkAreaDialog
        open={showDialog}
        handleClose={() => {
          setShowDialog(false);
        }}
        setFilter={setFilter}
        workAreas={helperData?.workArea || []}
      />

      <ConfirmDialogV2UI
        {...confirmDialogData}
        largeButtons={true}
        maxWidth="lg"
        fullWidth={true}
        onClose={hideConfirmDialog}
      />

      <ServiceMonitorReportDialog
        {...reportDialogData}
        maxWidth="sm"
        onClose={hideReportDialog}
        setRefetch={setRefetch}
      />
    </PhoneBase>
  );
};

export default ServiceMonitorPage;
